/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { TooltipWrapper } from '../../display/TooltipWrapper';
import { Spinner } from '../../display/Spinner';
import { Animation, Colors } from '../../../theme';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';

type Props = {
  /**
   * Optional custom `aria-label` for the component. By default
   * `aria-label` will be computed based on the `checked` and
   * `indeterminate` props on this component and their corresponding
   * `*Text` props. However because that causes the `aria-label` to
   * change as `ToggleSwitch` is un/checked, that can be confusing
   * for screenreader users in some scenarios. In that case, use this
   * `ariaLabel` prop to clear up any ambiguity.
   */
  'aria-label'?: string;
  /**
   * Indicates whether the switch is checked on or off.
   */
  checked?: boolean;
  /**
   * Indicates whether the switch is disabled.
   */
  disabled?: boolean;
  /**
   * HTML `id` of the input.
   */
  id?: string;
  /**
   * Whether the component should display a loading state.
   */
  isLoading?: boolean;
  /**
   * HTML name attribute, typically used for the key that will be sent to the API.
   */
  name?: string;
  /**
   * Whether to show the "Disabled" tooltip when toggle is disabled.
   */
  showDisabledTooltip?: boolean;
  /**
   * Handler invoked when the switch changes from checked on or off.
   */
  onChange?: React.ChangeEventHandler;
  /**
   * HTML value attribute, i.e. the actual value of the input.
   */
  value?: string;
};

const Styles = labelStyles('ToggleSwitch', {
  root: (props: Props) => [
    css`
      align-items: stretch;
      color: ${Colors.GrayWarm10};
      cursor: pointer;
      display: inline-flex;
      overflow: hidden;
      /*
      PDL ToggleSwitches shouldn't have a border, so we're setting the border to the same color as the background to make it appear that it doesn't have a border.
      */
      border: 1px solid var(--zds-background-strongest);
      transition: all ${Animation.transitionDuration} ease-in-out;

      /*
      The PDL focus ring is rendered as pseudo-element with a border to support a smaller radius than the border-radius of the label.
      It is initially rendered without a border so that its transition can be animated.
      */
      &::before {
        content: '';
        position: absolute;
        top: -2px;
        left: -4px;
        right: -4px;
        bottom: -2px;
        border-radius: var(--zds-radius-small);
      }

      &:has(:focus-visible):not(:hover)::before {
        border: 2px solid var(--zds-ui-primary-stronger);
        border-radius: var(--zds-radius-small);
      }

      min-width: var(--zds-size-36);
      width: var(--zds-size-36);
      height: var(--zds-size-20);
      border-radius: 30px;
      /* Ensures that dot is correctly aligned to the left/right edge. */
      padding: var(--zds-border-width-default) var(--zds-size-2);
    `,
    !props.checked &&
      withModifier(
        'unchecked',
        css`
          background-color: var(--zds-background-strongest);
        `
      ),
    props.checked &&
      withModifier(
        'checked',
        css`
          background-color: var(--zds-ui-primary-stronger);
          border: 1px solid var(--zds-ui-primary-stronger);
        `
      ),
    (props.disabled || props.isLoading) &&
      withModifier(
        'disabled',
        css`
          background-color: ${Colors.GrayWarm2};
          color: ${Colors.GrayWarm5};
          cursor: not-allowed;
          border: 1px solid var(--zds-background-strongest);
        `
      ),
  ],

  innerWrap: css`
    position: relative;
    display: flex;
    justify-content: center;
    width: 100%;
  `,

  // Still needs to be "visible" so it can be tabbed to,
  // but it shouldn't render anything. Take it out of layout
  // flow and hide it with `opacity` for good measure. Also
  // stretch it across the container so tools like VoiceOver
  // show an outline in the correct location.
  input: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    z-index: -1;
  `,

  spinner: [
    css`
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: center;

      div[data-testid='spinner'] {
        height: var(--zds-size-16);
        width: var(--zds-size-16);
      }
    `,
  ],

  // This node's text should be centered within the available space
  // outside of the dot. For instance when the dot is on the left,
  // the text will be to the right of the dot, but centered in that
  // available space. When the dot is on the right, the text will be
  // to the left of the dot, but centered in that available space.
  text: (props: Props) => [
    css`
      display: grid;
      // Define to allow 'textInner' nodes to easily stack
      // on top of each other while preserving their widths,
      // setting up the layout for a nice transition.
      grid-template-areas: 'text';
      align-items: center;
      width: 100%;
      user-select: none;
      text-align: center;
      transition: padding ${Animation.transitionDuration} ease-in-out;
    `,
    !props.checked &&
      withModifier(`unchecked`, {
        paddingLeft: 'calc(30px + 8px)',
        paddingRight: 0,
      }),
    props.checked &&
      withModifier(`checked`, {
        paddingLeft: 0,
        paddingRight: 'calc(30px + 8px)',
      }),
    props.isLoading &&
      withModifier(
        'loading',
        css`
          visibility: hidden;
          height: 0;
        `
      ),
  ],

  // Render all text, stacked on top of each other, and then visibly hide
  // the `isHidden` text. This ensures that `ToggleSwitch` is as wide as
  // its longest text label, even if that label is hidden.
  textInner: ({ isHidden }: { isHidden: boolean }) => [
    css`
      // See note at 'grid-template-areas' definition.
      grid-area: text;
      font-size: var(--zds-space-0);
      transition: opacity 0.1s ease-in-out;
    `,
    isHidden &&
      withModifier(
        'hidden',
        css`
          opacity: 0;
        `
      ),
  ],

  dot: (props: Props) => [
    css`
      display: block;
      border-radius: 100%;
      background-color: var(--zds-gray-warm-6);
      position: absolute;
      top: 0;
      transition: all ${Animation.transitionDuration} ease-in-out;
      width: var(--zds-size-16);
      height: var(--zds-size-16);
    `,
    !props.checked &&
      withModifier(
        'unchecked',
        css`
          left: 0;
          transform: translateX(0);
        `
      ),
    props.checked &&
      // Using `left` and `transform` for proper `transition`.
      // Using `right: auto` for instance won't transition properly.
      // `translate` is used over `calc` since `translate`'s argument
      // is the size of the `dot` itself rather than its parent.
      withModifier(
        'checked',
        css`
          background-color: var(--zds-prime-white);
          left: 100%;
          transform: translateX(-100%);
        `
      ),
    props.disabled &&
      withModifier(
        'disabled',
        css`
          background-color: ${Colors.GrayWarm4};
        `
      ),
    props.isLoading &&
      withModifier(
        'loading',
        css`
          visibility: hidden;
          height: 0;
        `
      ),
  ],
});

const getAriaLabel = (props: Props) => {
  if (props['aria-label']) {
    return props['aria-label'];
  }
  if (props.isLoading) {
    return 'Loading';
  }
  return props.checked ? 'On' : 'Off';
};

const getTooltipContent = (props: Props) =>
  props.disabled && props.showDisabledTooltip ? 'Disabled' : '';

/**
 * `ToggleSwitch` is a stylized checkbox used to indicate whether something is on or off.
 */
export const ToggleSwitch = ({
  'aria-label': ariaLabel = undefined,
  checked = undefined,
  disabled = undefined,
  id = undefined,
  isLoading = false,
  name = undefined,
  onChange = () => {},
  showDisabledTooltip = true,
  value = undefined,
}: Props) => {
  const props = {
    'aria-label': ariaLabel,
    checked,
    disabled,
    id,
    isLoading,
    name,
    onChange,
    showDisabledTooltip,
    value,
  } satisfies Props;

  return (
    <TooltipWrapper content={getTooltipContent(props)}>
      {({ childProps }) => (
        <label {...childProps} css={Styles.root(props)} data-zds>
          <span css={Styles.innerWrap} data-zds>
            <input
              // Not all screenreaders announce the `disabled` attribute,
              // so add `aria-disabled` to reinforce.
              aria-disabled={props.disabled || props.isLoading}
              aria-label={getAriaLabel(props)}
              checked={props.checked}
              css={Styles.input}
              disabled={props.disabled || props.isLoading}
              data-zds
              id={props.id}
              name={props.name}
              onChange={props.onChange}
              type="checkbox"
              value={props.value}
            />
            {props.isLoading && (
              <span css={Styles.spinner} data-zds>
                <Spinner />
              </span>
            )}
            {!props.isLoading && (
              <span
                aria-hidden="true"
                css={Styles.dot(props)}
                role="presentation"
                data-zds
              />
            )}
          </span>
        </label>
      )}
    </TooltipWrapper>
  );
};
